import React, { useEffect, useState } from 'react';
import { Avatar, Button, Dropdown, Input, Modal, message, Tooltip } from 'antd';
import { RobotOutlined, SearchOutlined, SettingOutlined, LogoutOutlined, BgColorsOutlined, MenuOutlined, PlusOutlined, CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { chatApi } from '../services/api-services';
import { resolveUrl } from '../utils/url';
import Chat from '../pages/Chat';
import './QuickChat.css';

// 全局缓存，与Chat组件共享
let globalEmployeesCache: any[] | null = null;
let globalEmployeesPromise: Promise<any> | null = null;

const loadEmployeesOnce = () => {
  if (globalEmployeesCache) return Promise.resolve(globalEmployeesCache);
  if (globalEmployeesPromise) return globalEmployeesPromise;
  
  globalEmployeesPromise = chatApi.listEmployees().then((res: any) => {
    const list = Array.isArray(res) ? res : res?.items ?? res?.data ?? [];
    globalEmployeesCache = list;
    globalEmployeesPromise = null;
    return list;
  }).catch((err) => {
    globalEmployeesPromise = null;
    throw err;
  });
  
  return globalEmployeesPromise;
};

interface ChatTab {
  id: string; // unique tab id
  employeeId: string | undefined;
  name: string;
  avatar?: string;
  sessionId?: string; // for saved sessions
}

interface SavedSession {
  session_id: string;
  saved_name: string;
  employee_id: number | null;
  employee_name: string | null;
  employee_avatar: string | null;
}

const ScrollDesc: React.FC<{ text: string; hovering?: boolean }> = ({ text, hovering }) => {
  const outerRef = React.useRef<HTMLSpanElement>(null);
  const innerRef = React.useRef<HTMLSpanElement>(null);

  React.useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;
    if (hovering) {
      const overflow = inner.scrollWidth - outer.clientWidth;
      if (overflow <= 0) return;
      const duration = overflow / 20;
      const timer = setTimeout(() => {
        inner.style.transition = `transform ${duration}s linear`;
        inner.style.transform = `translateX(-${overflow}px)`;
      }, 200);
      return () => { clearTimeout(timer); };
    } else {
      inner.style.transition = 'none';
      inner.style.transform = 'translateX(0)';
    }
  }, [hovering]);

  return (
    <span className="qc-sidebar-item-desc" ref={outerRef}>
      <span className="qc-sidebar-item-desc-inner" ref={innerRef}>{text}</span>
    </span>
  );
};

const EmployeeItem: React.FC<{ employee: any; onSelect: () => void }> = ({ employee: e, onSelect }) => {
  const [hovering, setHovering] = useState(false);
  return (
    <div
      className="qc-sidebar-item"
      onClick={onSelect}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <Avatar size={28} src={resolveUrl(e.avatar)}>{e.name?.charAt(0)}</Avatar>
      <div className="qc-sidebar-item-info">
        <span className="qc-sidebar-item-name">{e.name}</span>
        {e.description && <ScrollDesc text={e.description} hovering={hovering} />}
      </div>
    </div>
  );
};

// 同步 URL 参数（不产生历史记录）
const syncUrl = (tabId: string) => {
  const url = new URL(window.location.href);
  if (tabId === 'default') {
    url.searchParams.delete('employee');
    url.searchParams.delete('session');
  } else if (tabId.startsWith('session:')) {
    url.searchParams.delete('employee');
    url.searchParams.set('session', tabId.slice(8));
  } else {
    url.searchParams.set('employee', tabId);
    url.searchParams.delete('session');
  }
  window.history.replaceState(null, '', url.toString());
};

const QuickChat: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme, toggleTheme } = useTheme();
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [employeeExpanded, setEmployeeExpanded] = useState(true);
  const [tabsExpanded, setTabsExpanded] = useState(true);
  const [savedSessions, setSavedSessions] = useState<SavedSession[]>([]);
  const [savedSessionsExpanded, setSavedSessionsExpanded] = useState(true);
  const searchInputRef = React.useRef<any>(null);
  const employeesLoadedRef = React.useRef(false);
  const urlInitializedRef = React.useRef(false);
  // 每个 Chat 实例挂载后注册 focusInput 回调，切换 tab 时自动聚焦
  const focusCallbacksRef = React.useRef<Record<string, () => void>>({});

  // 从 URL 读取初始参数
  const urlEmployee = searchParams.get('employee');
  const urlSession = searchParams.get('session');

  // Multi-chat tabs: keep all opened chats alive
  const initialTabId = urlEmployee ? urlEmployee : urlSession ? `session:${urlSession}` : 'default';
  const [chatTabs, setChatTabs] = useState<ChatTab[]>([
    { id: 'default', employeeId: undefined, name: '默认数字人' }
  ]);
  const [activeTabId, setActiveTabId] = useState(initialTabId);

  useEffect(() => {
    if (!localStorage.getItem('token') || employeesLoadedRef.current) return;
    employeesLoadedRef.current = true;
    loadEmployeesOnce().then((list: any[]) => {
      setEmployees(list);
      setLoading(false);
      // URL 有 employee 参数时，用加载到的数据初始化 tab
      if (urlEmployee && !urlInitializedRef.current) {
        urlInitializedRef.current = true;
        const emp = list.find((e: any) => String(e.id) === urlEmployee);
        if (emp) {
          setChatTabs(prev => {
            if (prev.find(t => t.id === urlEmployee)) return prev;
            return [...prev, { id: urlEmployee, employeeId: urlEmployee, name: emp.name, avatar: emp.avatar }];
          });
          setActiveTabId(urlEmployee);
        }
      }
    }).catch(() => setLoading(false));
    chatApi.listSavedSessions().then((data: any) => {
      const list = Array.isArray(data) ? data : [];
      setSavedSessions(list);
      // URL 有 session 参数时，用加载到的数据初始化 tab
      if (urlSession && !urlInitializedRef.current) {
        urlInitializedRef.current = true;
        const session = list.find((s: any) => s.session_id === urlSession);
        if (session) {
          const tabId = `session:${session.session_id}`;
          setChatTabs(prev => {
            if (prev.find(t => t.id === tabId)) return prev;
            return [...prev, {
              id: tabId,
              employeeId: session.employee_id ? String(session.employee_id) : undefined,
              name: session.saved_name,
              avatar: session.employee_avatar ?? undefined,
              sessionId: session.session_id,
            }];
          });
          setActiveTabId(tabId);
        }
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (sidebarOpen) {
      setSearchText('');
      setTimeout(() => searchInputRef.current?.focus(), 150);
    }
  }, [sidebarOpen]);

  // 切换到新 tab 后聚焦输入框
  useEffect(() => {
    const cb = focusCallbacksRef.current[activeTabId];
    if (cb) {
      setTimeout(cb, 80);
    }
  }, [activeTabId]);

  if (!localStorage.getItem('token')) {
    return <Navigate to={`/login?redirect=${encodeURIComponent('/quick-chat')}`} replace />;
  }

  const activeTab = chatTabs.find(t => t.id === activeTabId);

  const filteredEmployees = employees.filter((e) =>
    e.name.toLowerCase().includes(searchText.toLowerCase())
  );

  // Open or switch to a chat with given employee
  const openChat = (employeeId: string | undefined, name: string, avatar?: string) => {
    const tabId = employeeId ?? 'default';
    const existing = chatTabs.find(t => t.id === tabId);
    if (!existing) {
      setChatTabs(prev => [...prev, { id: tabId, employeeId, name, avatar }]);
    }
    setActiveTabId(tabId);
    syncUrl(tabId);
    setSidebarOpen(false);
  };

  const openSavedSession = (session: SavedSession) => {
    const tabId = `session:${session.session_id}`;
    const existing = chatTabs.find(t => t.id === tabId);
    if (!existing) {
      setChatTabs(prev => [...prev, {
        id: tabId,
        employeeId: session.employee_id ? String(session.employee_id) : undefined,
        name: session.saved_name,
        avatar: session.employee_avatar ?? undefined,
        sessionId: session.session_id,
      }]);
    }
    setActiveTabId(tabId);
    syncUrl(tabId);
    setSidebarOpen(false);
  };

  const deleteSession = (e: React.MouseEvent, session: SavedSession) => {
    e.stopPropagation();
    Modal.confirm({
      title: '删除会话',
      content: '确定要删除这个会话吗？',
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        try {
          await chatApi.deleteSession(session.session_id);
          message.success('删除成功');
          setSavedSessions(prev => prev.filter(s => s.session_id !== session.session_id));
          // 如果该会话 tab 已打开则关闭
          const tabId = `session:${session.session_id}`;
          setChatTabs(prev => {
            const next = prev.filter(t => t.id !== tabId);
            if (activeTabId === tabId) {
              const fallback = next.length > 0 ? next[next.length - 1] : { id: 'default', employeeId: undefined, name: '默认数字人' };
              setActiveTabId(fallback.id);
              return next.length > 0 ? next : [fallback];
            }
            return next.length > 0 ? next : prev;
          });
        } catch {
          message.error('删除失败');
        }
      },
    });
  };

  const closeTab = (tabId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setChatTabs(prev => {
      const next = prev.filter(t => t.id !== tabId);
      if (next.length === 0) {
        syncUrl('default');
        setActiveTabId('default');
        return [{ id: 'default', employeeId: undefined, name: '默认数字人' }];
      }
      if (activeTabId === tabId) {
        const fallback = next[next.length - 1];
        syncUrl(fallback.id);
        setActiveTabId(fallback.id);
      }
      return next;
    });
  };

  return (
    <div className="quick-chat-page">
      {/* Sidebar overlay */}
      {sidebarOpen && <div className="qc-sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <div className={`qc-sidebar ${sidebarOpen ? 'qc-sidebar--open' : ''}`}>
        <div className="qc-sidebar-header">
          <span className="qc-sidebar-title">聊天列表</span>
        </div>

        <div style={{ padding: '8px 12px' }}>
          <Input
            ref={searchInputRef}
            placeholder="搜索"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            size="middle"
          />
        </div>

        {/* Active chat tabs */}
        <div className="qc-sidebar-section">
          <div className="qc-sidebar-section-label qc-employee-header" style={{ cursor: 'pointer', userSelect: 'none' as const }} onClick={() => setTabsExpanded(v => !v)}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 10, color: 'rgba(0,0,0,0.45)', transition: 'transform 0.2s', display: 'inline-block', transform: tabsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}>▼</span>
              临时会话
            </span>
          </div>
          {tabsExpanded && chatTabs.filter(tab => !tab.sessionId).map(tab => (
            <div
              key={tab.id}
              className={`qc-sidebar-item ${tab.id === activeTabId ? 'qc-sidebar-item--active' : ''}`}
              onClick={() => { setActiveTabId(tab.id); syncUrl(tab.id); setSidebarOpen(false); }}
            >
              <Avatar size={28} src={tab.avatar ? resolveUrl(tab.avatar) : undefined} icon={!tab.avatar ? <RobotOutlined /> : undefined}>
                {tab.name?.charAt(0)}
              </Avatar>
              <span className="qc-sidebar-item-name">{tab.name}</span>
              {tab.id !== 'default' && (
                <CloseOutlined className="qc-sidebar-item-close" onClick={(e) => closeTab(tab.id, e)} />
              )}
            </div>
          ))}
        </div>

        {/* Saved sessions */}
        {savedSessions.length > 0 && (
          <div className="qc-sidebar-section">
            <div className="qc-sidebar-section-label qc-employee-header" style={{ cursor: 'pointer', userSelect: 'none' as const }} onClick={() => setSavedSessionsExpanded(v => !v)}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 10, color: 'rgba(0,0,0,0.45)', transition: 'transform 0.2s', display: 'inline-block', transform: savedSessionsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}>▼</span>
                会话列表
              </span>
            </div>
            {savedSessionsExpanded && savedSessions
              .filter(s => s.saved_name.toLowerCase().includes(searchText.toLowerCase()) || (s.employee_name ?? '').toLowerCase().includes(searchText.toLowerCase()))
              .map(session => (
                <div
                  key={session.session_id}
                  className={`qc-sidebar-item ${activeTabId === `session:${session.session_id}` ? 'qc-sidebar-item--active' : ''}`}
                  onClick={() => openSavedSession(session)}
                >
                  <Avatar size={28} src={session.employee_avatar ? resolveUrl(session.employee_avatar) : undefined} icon={!session.employee_avatar ? <RobotOutlined /> : undefined} />
                  <div className="qc-sidebar-item-info">
                    <span className="qc-sidebar-item-name">{session.saved_name}</span>
                    {session.employee_name && <span className="qc-sidebar-item-desc">{session.employee_name}</span>}
                  </div>
                  <DeleteOutlined className="qc-sidebar-item-close" onClick={(e) => deleteSession(e, session)} />
                </div>
              ))
            }
          </div>
        )}

        {/* Employee list for starting new chats */}
        <div className="qc-sidebar-section" style={{ flex: employeeExpanded ? 1 : 'none', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div
            className="qc-sidebar-section-label qc-employee-header"
            style={{ cursor: 'pointer', userSelect: 'none' }}
            onClick={() => setEmployeeExpanded(v => !v)}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 10, color: 'rgba(0,0,0,0.45)', transition: 'transform 0.2s', display: 'inline-block', transform: employeeExpanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}>▼</span>
              数字人列表
            </span>
          </div>
          {employeeExpanded && (
            <div className="qc-sidebar-employee-list">
            {loading ? (
              <div className="qc-sidebar-loading">加载中...</div>
            ) : (
              filteredEmployees.map((e) => (
                <EmployeeItem key={e.id} employee={e} onSelect={() => openChat(String(e.id), e.name, e.avatar)} />
              ))
            )}
          </div>
          )}
        </div>
      </div>

      {/* Header */}
      <div className="quick-chat-header">
        <div className="quick-chat-header-left">
          <Button
            type="text"
            icon={<MenuOutlined />}
            className="qc-menu-btn"
            onClick={() => setSidebarOpen(true)}
          />
          {activeTab && activeTab.employeeId ? (
            <span className="quick-chat-employee-name">与「{activeTab.name}」聊天中</span>
          ) : (
            <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>默认数字人</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'theme',
                  icon: <BgColorsOutlined />,
                  label: '主题切换',
                  onClick: () => toggleTheme(),
                },
                {
                  key: 'logout',
                  icon: <LogoutOutlined />,
                  label: '退出登录',
                  danger: true,
                  onClick: () => {
                    localStorage.removeItem('token');
                    navigate('/login');
                  },
                },
              ],
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button type="text" icon={<SettingOutlined />} className="quick-chat-settings-btn" />
          </Dropdown>
        </div>
      </div>

      {/* Chat body - all tabs rendered, only active one visible */}
      <div className="quick-chat-body">
        {chatTabs.map(tab => (
          <div
            key={tab.id}
            className="qc-chat-instance"
            style={{ display: tab.id === activeTabId ? 'flex' : 'none' }}
          >
            <Chat
              employeeId={tab.employeeId}
              quickMode
              onSwitchEmployee={() => setSidebarOpen(true)}
              initialSessionId={tab.sessionId}
              storeKey={tab.id}
              onMounted={(focusInput) => { focusCallbacksRef.current[tab.id] = focusInput; }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default QuickChat;
